import type { SxProps, Theme } from "@mui/material/styles";
import { faInfoCircle } from "@outschool/icons";
import React from "react";

import Box from "../Box/Box";
import Icon from "../Icon/Icon";
import Tooltip from "../Tooltip/Tooltip";
import Typography from "../Typography/Typography";

export type OsFieldProps = {
  /** The label content */
  label?: React.ReactNode;
  /** Optional description text displayed below the label */
  description?: React.ReactNode;
  /** When provided, renders an info circle icon next to the label that shows this content as a tooltip */
  tooltipText?: React.ReactNode;
  /** Persistent helper text displayed below the input */
  helperText?: React.ReactNode;
  /** Error message displayed below the input. When truthy, triggers error styling. */
  errorMessage?: React.ReactNode;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** If true, the form control takes the full width of its container */
  fullWidth?: boolean;
  /** The input element(s) to render */
  children: React.ReactNode;
};

const labelSx: SxProps<Theme> = {
  typography: "body1",
  fontWeight: "fontWeightMedium",
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  color: "grey.900",
  mb: t => t.spacing(8),
};

const disabledLabelSx: SxProps<Theme> = {
  color: "grey.500",
};

const requiredAsteriskSx: SxProps<Theme> = {
  color: "error.600",
  letterSpacing: "0.1px",
};

const helperTextBaseSx: SxProps<Theme> = {
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  mt: t => t.spacing(4),
};

/**
 * Component to wrap an input with common form field elements (label, help text, error messages, etc.)
 */
export const OsField = ({
  label,
  description,
  tooltipText,
  helperText,
  errorMessage,
  required,
  disabled,
  fullWidth,
  children,
}: OsFieldProps) => {
  const rootSx: SxProps<Theme> = {
    display: "inline-flex",
    flexDirection: "column",
    width: fullWidth ? "100%" : undefined,
  };

  const hasEnhancedLabel = label != null && (tooltipText || description);

  const renderedLabel = label != null && (
    <Box
      component="label"
      sx={[labelSx, disabled ? disabledLabelSx : false].filter(Boolean)}
    >
      {hasEnhancedLabel ? (
        <>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "baseline",
                gap: "2px",
              }}
            >
              <span>{label}</span>
              {required && (
                <Box component="span" sx={requiredAsteriskSx}>
                  *
                </Box>
              )}
            </Box>
            {tooltipText && (
              <Tooltip title={tooltipText} placement="right">
                <Box
                  component="span"
                  sx={{ display: "inline-flex", cursor: "help" }}
                >
                  <Icon
                    icon={faInfoCircle}
                    color="primary"
                    sx={{ fontSize: 16 }}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
          {description && (
            <Typography
              variant="body2"
              color="grey.600"
              component="span"
              sx={{
                display: "block",
                fontWeight: "normal",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
              }}
            >
              {description}
            </Typography>
          )}
        </>
      ) : (
        <>
          <span>{label}</span>
          {required && (
            <Box component="span" sx={requiredAsteriskSx}>
              *
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box sx={rootSx}>
      {renderedLabel}
      {children}
      {errorMessage && (
        <Typography
          variant="body2"
          color="error.main"
          component="p"
          sx={helperTextBaseSx}
        >
          {errorMessage}
        </Typography>
      )}
      {helperText && (
        <Typography
          variant="body2"
          color="grey.600"
          component="p"
          sx={helperTextBaseSx}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};
